import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  ILicenseQuestionDetail,
  LicenseQuestionRequest,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import SelectQuestionValidationRule from "./SelectQuestionValidationRule";

const { Form } = withTypes<LicenseQuestionRequest>();

type LicenseQuestionFormProps = {
  licenseId: string;
  question: ILicenseQuestionDetail;
  isRootOrganization: boolean;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const LicenseQuestionForm = ({
  licenseId,
  question,
  isRootOrganization,
  open,
  onClose,
  onSaved,
}: LicenseQuestionFormProps) => {
  const { formatMessage: t } = useIntl();
  const { licenseService } = useGlobalContext();

  const init = {
    value: question.defaultValue ?? "",
    required: question.required,
    sequence: question.sequence,
    questionValidationRuleId: question.questionValidationRuleId,
  };

  const handleSubmit = async (values: LicenseQuestionRequest) => {
    try {
      const request = LicenseQuestionRequest.fromJS({
        id: isRootOrganization
          ? question.licenseQuestionId
          : question.organizationLicenseQuestionId,
        licenseQuestionId: question.licenseQuestionId,
        questionValidationRuleId: values.questionValidationRuleId || undefined,
        value: values.value,
        required: !!values.required,
        sequence: Number(values.sequence) || 0,
      });

      await licenseService.saveLicenseQuestion(licenseId, request);
      onSaved();
      onClose();
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Form
        onSubmit={handleSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit} noValidate>
            <DialogTitle>{t({ id: "editLicenseQuestion" })}</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    variant="standard"
                    fullWidth
                    disabled
                    label={t({ id: "questionLabel" })}
                    value={question.question ?? ""}
                  />
                </Grid>

                <Field name="value" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        required
                        variant="standard"
                        fullWidth
                        label={t({ id: "defaultValueLabel" })}
                        error={meta.error && meta.submitFailed}
                        helperText={meta.error && meta.submitFailed && meta.error}
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="questionValidationRuleId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl fullWidth error={meta.error && meta.submitFailed}>
                        <SelectQuestionValidationRule
                          label={t({ id: "validationRule" })}
                          value={input.value || undefined}
                          onChange={input.onChange}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field name="sequence">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "sequenceLabel" })}
                        InputProps={{ type: "number" }}
                        error={meta.error && meta.submitFailed}
                        helperText={meta.error && meta.submitFailed && meta.error}
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="required" type="checkbox">
                  {({ input }) => (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={input.checked}
                            onChange={input.onChange}
                          />
                        }
                        label={t({ id: "requiredLabel" })}
                      />
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Grid item xs={12}>
                    <Alert severity="error">{submitError}</Alert>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button disabled={submitting} onClick={onClose}>
                {t({ id: "cancelButton" })}
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                startIcon={submitting && <CircularProgress size={16} />}
              >
                {t({ id: "saveButton" })}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

export default LicenseQuestionForm;
